import { Location } from "@angular/common";
import { Injectable, inject } from "@angular/core";

import { ProductStoryDeprecated } from "@hermes/api-model-editorial";
import { COUNTRY } from "@hermes/app-core";
import { Country } from "@hermes/locale";
import { AnalyticsService, THUMBNAIL_IMAGE } from "@hermes/fragments/analytics";

import { SCENE7_HERMES_PRODUCT_REGEX } from "@hermes/utils-generic/constants";
import { getMatchAtIndex } from "@hermes/utils-generic/helpers";

import { getProductSize } from "./push-merch-analytics.helpers";

import {
    Impressions,
    ProductAnalytics,
    ProductClick,
} from "./push-merch-analytics.model";

@Injectable()
export class PushMerchAnalyticsService {
    private analyticsService = inject(AnalyticsService);
    private location = inject(Location);
    country = inject<Country>(COUNTRY);

    public sendImpression(
        productList: ProductStoryDeprecated[],
        pushMerchPosition: number,
    ): void {
        const productAnalytics: ProductAnalytics[] = productList.map(
            (product, index) =>
                this.productStoryMapper(
                    product,
                    index + 1,
                    this.getMerchandisingPosition(pushMerchPosition),
                ),
        );

        this.analyticsService.sendRawData<Impressions>({
            event: "impressions",
            ecommerce: {
                currencyCode: this.country.currency,
                impressions: productAnalytics,
            },
        });
    }

    public sendProductClick(
        product: ProductStoryDeprecated,
        position: number,
        pushMerchPosition: number,
    ): void {
        this.analyticsService.sendRawData<ProductClick>({
            event: "productClick",
            ecommerce: {
                currencyCode: this.country.currency,
                click: {
                    products: [this.productStoryMapper(product, position)],
                    actionField: {
                        list: this.getMerchandisingPosition(pushMerchPosition),
                    },
                },
            },
        });
    }

    private productStoryMapper(
        productStory: ProductStoryDeprecated,
        position: number,
        list?: string,
    ): ProductAnalytics {
        const productAnalytics: ProductAnalytics = {
            name: productStory.title,
            id: productStory.sku,
            price: productStory.price,
            brand: "hermes",
            position,
            category: productStory.category,
            variant: productStory.variant,
            dimension5: getProductSize(productStory.size),
            dimension6: productStory.departementCode,
            dimension7: productStory.familyCode,
            dimension8: productStory.productCode,
            dimension9: productStory.divisionCode,
            dimension37: productStory.stock,
            dimension52: productStory.personalization,
            dimension53: productStory.productAvailability,
            dimension62: productStory.assets[0]?.url.includes("composite")
                ? ""
                : getMatchAtIndex(
                      productStory.assets[0]?.url,
                      SCENE7_HERMES_PRODUCT_REGEX,
                      1,
                  ),
            dimension63: THUMBNAIL_IMAGE,
        };
        if (list) {
            productAnalytics.list = list;
        }

        return productAnalytics;
    }

    private getMerchandisingPosition(position: number): string {
        return this.location.path().startsWith("/?") ||
            this.location.path() === "/"
            ? `merchandising_homepage_${position}`
            : `merchandising_edito_page_${position}`;
    }
}
